import React, { useState, useEffect } from 'react';
import secService from '../services/secService';
import SECScheduleForm from '../components/SECScheduleForm';
import SECScheduleList from '../components/SECScheduleList';
import SECFilingList from '../components/SECFilingList';
import SECFilingAnalysisModal from '../components/SECFilingAnalysisModal';
import SECAnalysisJobsTable from '../components/SECAnalysisJobsTable';
import SECBurstPollModal from '../components/SECBurstPollModal';
import SECBurstSessionsList from '../components/SECBurstSessionsList';

const SECFilingPage = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('schedules');

  // Schedules state
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal states for schedules
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [isEditingSchedule, setIsEditingSchedule] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  // Filings state
  const [filings, setFilings] = useState([]);
  const [filingsLoading, setFilingsLoading] = useState(false);

  // Analysis modal state
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [filingsToAnalyze, setFilingsToAnalyze] = useState([]);

  // Polling state
  const [pollingScheduleId, setPollingScheduleId] = useState(null);
  const [backfillingScheduleId, setBackfillingScheduleId] = useState(null);

  // Burst polling state
  const [burstSessions, setBurstSessions] = useState([]);
  const [burstSessionsLoading, setBurstSessionsLoading] = useState(false);
  const [showBurstModal, setShowBurstModal] = useState(false);
  const [selectedScheduleForBurst, setSelectedScheduleForBurst] = useState(null);
  const [deletingSessionId, setDeletingSessionId] = useState(null);

  useEffect(() => {
    fetchSchedules();
    if (activeTab === 'filings') {
      fetchFilings();
    }
    if (activeTab === 'burst') {
      fetchBurstSessions();
    }
  }, [activeTab]);

  // Auto-refresh burst sessions every 5 seconds when viewing the burst tab and there are active sessions
  useEffect(() => {
    if (activeTab !== 'burst') return;
    
    const hasActiveSessions = burstSessions.some(s => s.status === 'active' || s.status === 'scheduled');
    if (!hasActiveSessions) return;

    const intervalId = setInterval(() => {
      fetchBurstSessions();
    }, 5000);

    return () => clearInterval(intervalId);
  }, [activeTab, burstSessions]);

  const fetchSchedules = async () => {
    try {
      setSchedulesLoading(true);
      const data = await secService.getAllSchedules();
      setSchedules(data.schedules || []);
      setError('');
    } catch (err) {
      console.error('Error fetching schedules:', err);
      setError('Failed to load schedules: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSchedulesLoading(false);
    }
  };

  const fetchFilings = async () => {
    try {
      setFilingsLoading(true);
      const data = await secService.getRecentFilings();
      setFilings(data.filings || []);
      setError('');
    } catch (err) {
      console.error('Error fetching filings:', err);
      setError('Failed to load filings: ' + (err.response?.data?.detail || err.message));
    } finally {
      setFilingsLoading(false);
    }
  };

  const handleCreateSchedule = () => {
    setIsEditingSchedule(false);
    setSelectedSchedule(null);
    setShowScheduleModal(true);
  };

  const handleEditSchedule = (schedule) => {
    setIsEditingSchedule(true);
    setSelectedSchedule(schedule);
    setShowScheduleModal(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) {
      return;
    }

    try {
      await secService.deleteSchedule(scheduleId);
      await fetchSchedules();
    } catch (err) {
      console.error('Error deleting schedule:', err);
      alert('Failed to delete schedule: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSaveSchedule = async (formData) => {
    try {
      if (isEditingSchedule && selectedSchedule) {
        await secService.updateSchedule(selectedSchedule.schedule_id, formData);
      } else {
        await secService.createSchedule(formData);
      }
      setShowScheduleModal(false);
      await fetchSchedules();
    } catch (err) {
      console.error('Error saving schedule:', err);
      throw err;
    }
  };

  const handleTriggerPoll = async (scheduleId) => {
    try {
      setPollingScheduleId(scheduleId);
      await secService.triggerPoll(scheduleId);
      alert('Poll triggered successfully! Check the Filings tab to see results.');
      
      // Refresh schedules to update last_poll_time
      await fetchSchedules();
      
      // If we're on the filings tab, refresh filings too
      if (activeTab === 'filings') {
        await fetchFilings();
      }
    } catch (err) {
      console.error('Error triggering poll:', err);
      alert('Failed to trigger poll: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPollingScheduleId(null);
    }
  };

  const handleBackfill = async (scheduleId) => {
    try {
      setBackfillingScheduleId(scheduleId);
      await secService.backfillSchedule(scheduleId);
      alert('Backfill triggered successfully! Last 50 filings will be processed. Check the Filings tab to see results.');
      
      // Refresh schedules
      await fetchSchedules();
      
      // If we're on the filings tab, refresh filings too
      if (activeTab === 'filings') {
        await fetchFilings();
      }
    } catch (err) {
      console.error('Error triggering backfill:', err);
      alert('Failed to trigger backfill: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBackfillingScheduleId(null);
    }
  };

  const handleViewFilings = () => {
    setActiveTab('filings');
    fetchFilings();
  };

  const handleAnalyzeFilings = (filings) => {
    setFilingsToAnalyze(filings);
    setShowAnalysisModal(true);
  };

  // Burst polling handlers
  const fetchBurstSessions = async () => {
    try {
      setBurstSessionsLoading(true);
      const data = await secService.getAllBurstSessions();
      setBurstSessions(data.sessions || []);
    } catch (err) {
      console.error('Error fetching burst sessions:', err);
      setError('Failed to load burst sessions: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBurstSessionsLoading(false);
    }
  };

  const handleScheduleBurst = (schedule) => {
    setSelectedScheduleForBurst(schedule);
    setShowBurstModal(true);
  };

  const handleCreateBurstSession = async (sessionData) => {
    await secService.createBurstSession(sessionData);
    await fetchBurstSessions();
  };

  const handleDeleteBurstSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this burst session?')) {
      return;
    }

    try {
      setDeletingSessionId(sessionId);
      await secService.deleteBurstSession(sessionId);
      await fetchBurstSessions();
    } catch (err) {
      console.error('Error deleting burst session:', err);
      alert('Failed to delete burst session: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingSessionId(null);
    }
  };

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>SEC Filing Manager</h2>
        {activeTab === 'schedules' && (
          <button className="btn btn-primary" onClick={handleCreateSchedule}>
            <i className="bi bi-plus-circle me-2"></i>
            New Schedule
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError('')}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'schedules' ? 'active' : ''}`}
            onClick={() => setActiveTab('schedules')}
          >
            <i className="bi bi-calendar3 me-2"></i>
            Schedules
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'burst' ? 'active' : ''}`}
            onClick={() => setActiveTab('burst')}
          >
            <i className="bi bi-lightning-charge me-2"></i>
            Burst Sessions
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'filings' ? 'active' : ''}`}
            onClick={() => setActiveTab('filings')}
          >
            <i className="bi bi-file-earmark-text me-2"></i>
            Filings
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'analysis' ? 'active' : ''}`}
            onClick={() => setActiveTab('analysis')}
          >
            <i className="bi bi-graph-up me-2"></i>
            Analysis
          </button>
        </li>
      </ul>

      {/* Tab Content */}
      {activeTab === 'schedules' && (
        <SECScheduleList
          schedules={schedules}
          loading={schedulesLoading}
          onEdit={handleEditSchedule}
          onDelete={handleDeleteSchedule}
          onTriggerPoll={handleTriggerPoll}
          onBackfill={handleBackfill}
          onViewFilings={handleViewFilings}
          onScheduleBurst={handleScheduleBurst}
          pollingScheduleId={pollingScheduleId}
          backfillingScheduleId={backfillingScheduleId}
        />
      )}

      {activeTab === 'burst' && (
        <SECBurstSessionsList
          sessions={burstSessions}
          loading={burstSessionsLoading}
          onDelete={handleDeleteBurstSession}
          deletingSessionId={deletingSessionId}
        />
      )}

      {activeTab === 'filings' && (
        <SECFilingList
          filings={filings}
          loading={filingsLoading}
          onAnalyzeFilings={handleAnalyzeFilings}
        />
      )}

      {activeTab === 'analysis' && (
        <SECAnalysisJobsTable category="SEC Filings Analysis" />
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isEditingSchedule ? 'Edit Schedule' : 'Create New Schedule'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowScheduleModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <SECScheduleForm
                  schedule={selectedSchedule}
                  onSave={handleSaveSchedule}
                  onCancel={() => setShowScheduleModal(false)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analysis Modal */}
      {showAnalysisModal && (
        <SECFilingAnalysisModal
          selectedFilings={filingsToAnalyze}
          onClose={() => {
            setShowAnalysisModal(false);
            setFilingsToAnalyze([]);
          }}
        />
      )}

      {/* Burst Poll Modal */}
      {showBurstModal && selectedScheduleForBurst && (
        <SECBurstPollModal
          schedule={selectedScheduleForBurst}
          onClose={() => {
            setShowBurstModal(false);
            setSelectedScheduleForBurst(null);
          }}
          onSubmit={handleCreateBurstSession}
        />
      )}
    </div>
  );
};

export default SECFilingPage;
